import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useBoardsContext } from '../context/BoardsContext'
import useBoardMutations from '../hooks/useBoardMutations'

function BoardSettingsModal({ onClose }) {
  const { currentBoard, selectedBoardId, permission } = useBoardsContext()
  const { updateBoard, deleteBoard } = useBoardMutations(selectedBoardId)
  const [name, setName] = useState(currentBoard?.name || '')
  const [isPublic, setIsPublic] = useState(currentBoard?.public || false)
  const [isFavorite, setIsFavorite] = useState(false)

  useEffect(() => {
    if (!selectedBoardId) return
    const fetchFavorite = async () => {
      const { FetchRequest } = await import('@rails/request.js')
      const request = new FetchRequest('get', `/kanban_boards/${selectedBoardId}/favorite_status`, {
        headers: { Accept: 'application/json' }
      })
      const response = await request.perform()
      if (response.ok) {
        const data = await response.json
        setIsFavorite(data.favorited)
      }
    }
    fetchFavorite()
  }, [selectedBoardId])

  const handleToggleFavorite = async () => {
    const { FetchRequest } = await import('@rails/request.js')
    const request = new FetchRequest('put', `/kanban_boards/${selectedBoardId}/toggle_favorite`, {
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
    })
    const response = await request.perform()
    if (response.ok) {
      const data = await response.json
      setIsFavorite(data.favorited)
    }
  }

  const handleSave = async () => {
    await updateBoard.mutateAsync({ name: name.trim(), public: isPublic })
    onClose()
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this board? This cannot be undone.')) return
    await deleteBoard.mutateAsync()
    window.location.href = '/kanban_boards'
  }

  return (
    <div className="kb-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Board Settings</h3>
          <button className="text-gray-400 hover:text-gray-600 text-lg" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Board Name</label>
            <input
              type="text"
              className="form-input w-full rounded-md border-gray-300 shadow-sm text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="board-public"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="rounded border-gray-300 text-blue-600"
            />
            <label htmlFor="board-public" className="text-sm text-gray-700">
              Public (visible to all account members)
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="board-favorite"
              checked={isFavorite}
              onChange={handleToggleFavorite}
              className="rounded border-gray-300 text-blue-600"
            />
            <label htmlFor="board-favorite" className="text-sm text-gray-700">
              Favorite
            </label>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          {permission === 'owner' && (
            <button
              className="text-sm text-red-500 hover:text-red-700"
              onClick={handleDelete}
            >
              Delete Board
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button className="inline-flex justify-center rounded-md px-4 py-2 bg-gray-100 text-sm leading-5 font-semibold text-gray-700 hover:bg-gray-50 active:bg-gray-200 cursor-pointer" onClick={onClose}>Cancel</button>
            <button
              className="inline-flex justify-center rounded-md px-4 py-2 bg-blue-100 text-sm leading-5 font-semibold text-blue-700 hover:bg-blue-50 active:bg-blue-200 cursor-pointer"
              onClick={handleSave}
              disabled={!name.trim() || updateBoard.isPending}
            >
              {updateBoard.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

BoardSettingsModal.propTypes = {
  onClose: PropTypes.func.isRequired
}

export default BoardSettingsModal
